/**
 * Spectrum PLP Card — Image carousel
 *
 * Wires `[data-spectrum-plp-carousel]` strips to their `[data-spectrum-plp-dots]`:
 *   - Updates the active dot as the user swipes/scrolls between slides.
 *   - Lets users click a dot to scroll to that slide.
 *
 * Works on:
 *   - touch devices: native horizontal swipe via scroll-snap CSS.
 *   - desktop: trackpad scroll, dot clicks, or arrow-key focus on a dot.
 *
 * Uses event delegation so it picks up cards added later (e.g. infinite scroll).
 *
 * Wrapped in an IIFE so top-level `const`s don't collide with other
 * classic scripts that happen to use the same identifier names
 * (e.g. CAROUSEL_SELECTOR / DOTS_SELECTOR in spectrum-product-tabs-carousel.js).
 */

(function () {
const CAROUSEL_SELECTOR = "[data-spectrum-plp-carousel]";
const DOTS_SELECTOR = "[data-spectrum-plp-dots]";

const setActiveDot = (dotsEl, index) => {
  if (!dotsEl) return;
  const dots = dotsEl.querySelectorAll("[data-spectrum-plp-dot]");
  dots.forEach((dot, i) => {
    const active = i === index;
    dot.classList.toggle("spectrum-plp-card__dot--active", active);
    dot.setAttribute("aria-selected", active ? "true" : "false");
  });
};

const findDotsForCarousel = (carousel) => {
  const media = carousel.closest(".spectrum-plp-card__media");
  return media ? media.querySelector(DOTS_SELECTOR) : null;
};

const findCarouselForDots = (dotsEl) => {
  const media = dotsEl.closest(".spectrum-plp-card__media");
  return media ? media.querySelector(CAROUSEL_SELECTOR) : null;
};

// Slides 2+ ship as empty slide wrappers carrying the image in data-defer-*
// attributes — no <img> node until the user interacts. The empty wrappers keep
// the carousel scrollable from first paint (so the first swipe scrolls, not
// just hydrates); this builds the real <img> into each on first intent.
const buildDeferredImg = (slide) => {
  const src = slide.dataset.deferSrc;
  if (!src) return;
  const img = document.createElement("img");
  img.className = "spectrum-plp-card__img";
  img.sizes = "(min-width: 768px) 240px, 180px";
  if (slide.dataset.deferSrcset) img.srcset = slide.dataset.deferSrcset;
  img.src = src;
  if (slide.dataset.deferAlt) img.alt = slide.dataset.deferAlt;
  if (slide.dataset.deferW) img.width = slide.dataset.deferW;
  if (slide.dataset.deferH) img.height = slide.dataset.deferH;
  img.loading = "lazy";
  img.decoding = "async";
  slide.appendChild(img);
  delete slide.dataset.deferSrc;
  delete slide.dataset.deferSrcset;
  delete slide.dataset.deferAlt;
  delete slide.dataset.deferW;
  delete slide.dataset.deferH;
};

const hydrateCarousel = (carousel) => {
  if (carousel.dataset.spectrumPlpCarouselHydrated === "true") return;
  carousel.dataset.spectrumPlpCarouselHydrated = "true";
  carousel
    .querySelectorAll(".spectrum-plp-card__slide[data-defer-src]")
    .forEach(buildDeferredImg);
};

const observeCarousel = (carousel) => {
  if (carousel.dataset.spectrumPlpCarouselReady === "true") return;
  carousel.dataset.spectrumPlpCarouselReady = "true";

  const slides = carousel.querySelectorAll(".spectrum-plp-card__slide");
  if (slides.length <= 1) return;

  const dotsEl = findDotsForCarousel(carousel);

  // True while the desktop hover-autoplay is driving the strip. The manual
  // wrap-to-first machinery (below) stays dormant while this is true so it never
  // fights autoplay's own loop-back. Always false on touch devices (no autoplay),
  // where the wrap is the only looping mechanism.
  let autoplayActive = false;

  // Build slides 2+ images on first user intent (hover / touch / keyboard
  // focus). The slide wrappers already exist and keep the carousel scrollable;
  // this fills in the <img> nodes once the user is likely to swipe.
  const onIntent = () => hydrateCarousel(carousel);
  carousel.addEventListener("pointerenter", onIntent, { once: true, passive: true });
  carousel.addEventListener("touchstart", onIntent, { once: true, passive: true });
  carousel.addEventListener("focusin", onIntent, { once: true });

  // Compute the active slide directly from scrollLeft. More reliable than
  // IntersectionObserver, which can miss threshold crossings during fast
  // snap scrolling on iOS Safari and desktop with trackpad momentum.
  let scheduled = false;
  const syncActiveDot = () => {
    const step = slides[0].offsetWidth;
    if (!step) return;
    const index = Math.min(
      slides.length - 1,
      Math.max(0, Math.round(carousel.scrollLeft / step))
    );
    setActiveDot(dotsEl, index);
    // Scrolled past slide 0 → the user is navigating; load all remaining
    // images so subsequent swipes are smooth.
    if (index > 0) hydrateCarousel(carousel);
  };

  carousel.addEventListener(
    "scroll",
    () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        syncActiveDot();
      });
    },
    { passive: true }
  );

  // Initial sync in case the carousel mounted at a non-zero scrollLeft.
  syncActiveDot();

  // Desktop hover autoplay: while the pointer is over the card, advance one
  // slide every 1500ms (looping). On leave, fade the strip out, jump back to
  // the first slide while hidden, then fade in. Gated to hover-capable /
  // fine-pointer devices and disabled under reduced-motion, so touch devices
  // keep their normal swipe behaviour untouched.
  const hoverCapable = window.matchMedia("(hover: hover) and (pointer: fine)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  if (hoverCapable.matches && !reduceMotion.matches) {
    const card = carousel.closest(".spectrum-plp-card") || carousel;
    const FADE = 220;
    let timer = null;
    let rafTween = null; // autoplay's own scroll animation — fully cancellable
    let cleanupT = null;
    // Set true when the shopper scrolls the strip themselves mid-hover. Autoplay
    // then stays paused for the REST of this hover; it only restarts when the
    // pointer leaves the card and enters again (mouseleave → mouseenter → start).
    let userPaused = false;

    const easeInOut = (t) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const ADVANCE_MS = 450;

    // Autoplay scrolls with OUR OWN rAF tween instead of the browser's
    // scrollTo({behavior:"smooth"}). A browser smooth-scroll can't be reliably
    // aborted mid-flight: writing scrollLeft doesn't cancel it, and it re-applies
    // its own offset AFTER our rAF each frame — so on hover-out it kept gliding
    // toward slide 0 under the fade (the visible "slide back to first"). Owning
    // the tween means a single cancelAnimationFrame stops it dead, with no
    // browser animation left to fight.
    const tweenTo = (target) => {
      cancelAnimationFrame(rafTween);
      const from = carousel.scrollLeft;
      const dist = target - from;
      if (Math.abs(dist) < 1) { rafTween = null; return; }
      const t0 = performance.now();
      const tick = () => {
        const p = Math.min(1, (performance.now() - t0) / ADVANCE_MS);
        // Instant per-frame write (scroll-behavior is forced to auto on hover-in).
        carousel.scrollLeft = from + dist * easeInOut(p);
        rafTween = p < 1 ? requestAnimationFrame(tick) : null;
      };
      rafTween = requestAnimationFrame(tick);
    };
    // Crossfade to the first slide. The tween is cancelled first, so the current
    // image is FROZEN (zero horizontal motion) while it fades OUT; only AFTER it is
    // fully invisible do we reposition to slide 0 and fade it back in. Because the
    // scroll reset happens strictly after opacity has reached 0, the reposition can
    // never be painted on screen — it can only ever read as a fade, never a slide.
    //   terminal=true  → hover-out: restore the CSS snap/smooth defaults.
    //   terminal=false → autoplay loop-back: stay in hover mode so autoplay keeps going.
    const crossfadeToFirst = (terminal) => {
      cancelAnimationFrame(rafTween);
      rafTween = null;
      clearTimeout(cleanupT);
      // Fade the frozen current image OUT (no scroll change → nothing moves sideways).
      carousel.style.transition = "opacity " + FADE + "ms ease";
      carousel.style.opacity = "0";
      cleanupT = setTimeout(() => {
        // Fully invisible now → reposition to the first slide completely unseen.
        carousel.style.transition = "none";
        carousel.style.scrollBehavior = "auto";
        carousel.style.scrollSnapType = "none";
        carousel.scrollLeft = 0;
        setActiveDot(dotsEl, 0);
        if (terminal) {
          carousel.style.scrollBehavior = "";
          carousel.style.scrollSnapType = "";
        }
        void carousel.offsetHeight;
        // Fade the first slide back IN.
        carousel.style.transition = "opacity " + FADE + "ms ease";
        carousel.style.opacity = "1";
        cleanupT = setTimeout(() => {
          carousel.style.transition = "";
          carousel.style.opacity = "";
        }, FADE + 80);
      }, FADE + 30);
    };
    const advance = () => {
      const step = slides[0].offsetWidth || 1;
      const cur = Math.round(carousel.scrollLeft / step);
      const next = (cur + 1) % slides.length;
      // Forward step → smooth tween slide. Loop-back to the first slide → crossfade
      // (never a long backward slide across the card).
      if (next === 0) crossfadeToFirst(false);
      else tweenTo(next * step);
    };

    // Cancel any tween/fade and restore the strip to rest: full opacity, snap +
    // smooth-scroll back to their CSS defaults.
    const clearFade = () => {
      cancelAnimationFrame(rafTween);
      rafTween = null;
      clearTimeout(cleanupT);
      carousel.style.transition = "";
      carousel.style.opacity = "";
      carousel.style.scrollBehavior = "";
      carousel.style.scrollSnapType = "";
    };
    const start = () => {
      // Re-entering mid-fade: kill the pending reset so the strip stays put.
      clearFade();
      // A fresh hover always begins unpaused — a manual scroll during the
      // PREVIOUS hover must not carry over into this one.
      userPaused = false;
      autoplayActive = true;
      if (timer) return;
      hydrateCarousel(carousel);
      // Our tween writes scrollLeft directly, so force instant scrolling (the
      // carousel's CSS is scroll-behavior:smooth) and drop mandatory snap so it
      // can't fight the per-frame writes. Both restored on hover-out.
      carousel.style.scrollBehavior = "auto";
      carousel.style.scrollSnapType = "none";
      // Jump to the 2nd image immediately on hover, then continue the autoplay loop
      // every 1500ms. (Previously the first advance only fired after a full 1500ms
      // interval, so the card sat on image 1 for a beat-and-a-half before moving.)
      advance();
      timer = setInterval(advance, 1500);
    };
    const stop = () => {
      autoplayActive = false;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      // Kill the autoplay tween AND any in-progress loop-back fade.
      cancelAnimationFrame(rafTween);
      rafTween = null;
      clearTimeout(cleanupT);
      const frozen = carousel.scrollLeft;
      // Already on the first slide (a quick hover that never advanced) → nothing
      // to reset, just restore the CSS defaults.
      if (frozen <= 2) {
        carousel.style.scrollSnapType = "";
        carousel.style.scrollBehavior = "";
        setActiveDot(dotsEl, 0);
        return;
      }
      // Fade the frozen current image OUT, reset to slide 0 while invisible, then
      // fade the first slide back IN — no horizontal motion is ever on screen.
      crossfadeToFirst(true);
    };
    card.addEventListener("mouseenter", start);
    card.addEventListener("mouseleave", stop);

    // Manual-scroll pause. The instant the shopper scrolls the strip themselves,
    // stop autoplay so our per-frame tween isn't overwriting their scroll (the
    // "gets stuck midway" symptom). We deliberately do NOT snap back to the first
    // slide — the strip stays exactly where they left it — and we hand scrolling
    // back to the browser by restoring the CSS snap/smooth defaults autoplay
    // overrode, so the manual scroll feels native and snaps when it settles.
    // Autoplay only resumes on the next hover-out → hover-in (start() clears the
    // flag).
    const pauseForUser = () => {
      if (userPaused) return;
      userPaused = true;
      autoplayActive = false;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      cancelAnimationFrame(rafTween);
      rafTween = null;
      clearTimeout(cleanupT);
      carousel.style.transition = "";
      carousel.style.opacity = "";
      carousel.style.scrollBehavior = "";
      carousel.style.scrollSnapType = "";
    };
    // Any wheel over the strip = the shopper taking over. Both a trackpad's
    // horizontal wheel AND a plain mouse wheel (which the browser converts to
    // horizontal scroll on this overflow-x strip) must hand control back, or
    // autoplay keeps fighting the scroll. Autoplay restarts on the next
    // hover-out → hover-in, so pausing on an incidental page-scroll wheel is
    // harmless.
    carousel.addEventListener("wheel", pauseForUser, { passive: true });
    // A touch/drag directly on the strip (hybrid hover-capable touch laptops)
    // natively scrolls it, so that's a manual-scroll intent too.
    carousel.addEventListener("touchstart", pauseForUser, { passive: true });
  }

  // ---- Wrap-to-first: manual scroll "loops" like the autoplay ----
  // Once the shopper is resting on the LAST slide and scrolls FORWARD again, jump
  // back to the first slide with a short crossfade (one clean jump — never a long
  // backward slide). Lives OUTSIDE the hover-autoplay block so it also works on
  // touch devices, which have no autoplay. Under reduced-motion it jumps instantly.
  const maxScroll = () => carousel.scrollWidth - carousel.clientWidth;
  const atEnd = () => carousel.scrollLeft >= maxScroll() - 4;
  const reduceMotionW = window.matchMedia("(prefers-reduced-motion: reduce)");
  let wrapping = false;
  const wrapToFirst = () => {
    if (wrapping || carousel.scrollLeft < 4) return;
    wrapping = true;
    const jump = () => {
      const prevSnap = carousel.style.scrollSnapType;
      const prevBeh = carousel.style.scrollBehavior;
      carousel.style.scrollBehavior = "auto";
      carousel.style.scrollSnapType = "none";
      carousel.scrollLeft = 0;
      setActiveDot(dotsEl, 0);
      carousel.style.scrollBehavior = prevBeh;
      carousel.style.scrollSnapType = prevSnap;
    };
    if (reduceMotionW.matches) {
      jump();
      wrapping = false;
      return;
    }
    // Crossfade like the autoplay loop-back (crossfadeToFirst) but SNAPPIER: fade
    // the frozen last image OUT, reposition to slide 0 strictly AFTER opacity hits 0
    // (so the move is never painted — it reads as a fade, never a slide), then fade
    // slide 0 back IN. Kept short so a manual swipe-past feels as quick as sliding
    // between adjacent images, not a slow dissolve.
    const FADE = 110;
    carousel.style.transition = "opacity " + FADE + "ms ease";
    carousel.style.opacity = "0";
    setTimeout(() => {
      jump();
      void carousel.offsetHeight;
      carousel.style.transition = "opacity " + FADE + "ms ease";
      carousel.style.opacity = "1";
      setTimeout(() => {
        carousel.style.transition = "";
        carousel.style.opacity = "";
        wrapping = false;
      }, FADE + 80);
    }, FADE + 30);
  };

  // Wrap only when a SWIPE is pushed PAST the last slide — no auto-loop while the
  // shopper is just resting on it, and no separate second "push". While the strip
  // is pinned at the end, a continued forward gesture (finger dragging past the
  // end, or forward trackpad/mouse-wheel overscroll) arms a wrap; it fires only
  // once the gesture and its momentum have fully STOPPED (settle), so the jump to
  // slide 0 can't be carried forward onto slide 2-3. Runs only in manual mode
  // (autoplay does its own loop) and only from the last slide.
  const SETTLE = 80; // gesture considered finished after this quiet gap
  let settleTimer = null;
  let pendingWrap = false;
  const armSettle = () => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      if (!autoplayActive && !wrapping && pendingWrap && atEnd()) wrapToFirst();
      pendingWrap = false;
    }, SETTLE);
  };
  carousel.addEventListener("scroll", armSettle, { passive: true });

  // Desktop: a forward wheel that arrives while the strip is ALREADY pinned at the
  // end is overscroll — the shopper pushing past the last slide within the same
  // swipe. deltaX (trackpad) or deltaY (a plain mouse wheel the browser converts to
  // horizontal scroll on this strip). The wheel that merely REACHES the end sees
  // scrollLeft still short of max, so it doesn't arm — only continued pushing does.
  carousel.addEventListener(
    "wheel",
    (e) => {
      if (!autoplayActive && atEnd()) {
        const fwd = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        if (fwd > 0) pendingWrap = true;
      }
      armSettle();
    },
    { passive: true }
  );

  // Touch: the finger keeps moving left after the strip is pinned at the end —
  // dragging "past" the last slide. Anchored at the moment the end is reached so the
  // earlier part of the same swipe (legitimately scrolling to the end) doesn't count.
  let endAnchorX = null;
  carousel.addEventListener(
    "touchstart",
    () => {
      endAnchorX = null;
    },
    { passive: true }
  );
  carousel.addEventListener(
    "touchmove",
    (e) => {
      if (e.touches[0]) {
        const x = e.touches[0].clientX;
        if (atEnd()) {
          if (endAnchorX == null) endAnchorX = x;
          else if (endAnchorX - x > 40) pendingWrap = true;
        } else {
          endAnchorX = null;
        }
      }
      armSettle();
    },
    { passive: true }
  );
  carousel.addEventListener("touchend", armSettle, { passive: true });
};

const initAllCarousels = (root = document) => {
  root.querySelectorAll(CAROUSEL_SELECTOR).forEach(observeCarousel);
};

// Delegate dot clicks → scroll the carousel to the picked slide.
document.addEventListener("click", (e) => {
  const dot = e.target.closest("[data-spectrum-plp-dot]");
  if (!dot) return;
  const dotsEl = dot.closest(DOTS_SELECTOR);
  const carousel = findCarouselForDots(dotsEl);
  if (!carousel) return;
  e.preventDefault();
  // User picked a different slide — make sure the image is loaded before we scroll.
  hydrateCarousel(carousel);
  const index = Number(dot.dataset.slideIndex || 0);
  const slide = carousel.querySelector(`[data-slide-index="${index}"]`);
  if (slide) {
    carousel.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
  }
});

// Catch carousels added by infinite scroll or section reloads.
const mo = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches?.(CAROUSEL_SELECTOR)) {
        observeCarousel(node);
      } else {
        node.querySelectorAll?.(CAROUSEL_SELECTOR).forEach(observeCarousel);
      }
    }
  }
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initAllCarousels();
    mo.observe(document.body, { childList: true, subtree: true });
  });
} else {
  initAllCarousels();
  mo.observe(document.body, { childList: true, subtree: true });
}
})();
